import React, { useState, useCallback } from 'react'
import { AvailabilityWindow, DOW } from '../../types/availability'
import { normalizeWindows, buildWeekGrid } from '../../lib/availability'

interface AvailabilityEditorProps {
  windows: AvailabilityWindow[]
  onChange: (windows: AvailabilityWindow[]) => void
  className?: string
}

const PRESETS = [
  { name: 'Werkdagen ochtend', windows: [
    { dow: 1 as DOW, start: '09:00', end: '12:00' },
    { dow: 2 as DOW, start: '09:00', end: '12:00' },
    { dow: 3 as DOW, start: '09:00', end: '12:00' },
    { dow: 4 as DOW, start: '09:00', end: '12:00' },
    { dow: 5 as DOW, start: '09:00', end: '12:00' }
  ]},
  { name: 'Werkdagen middag', windows: [
    { dow: 1 as DOW, start: '13:00', end: '17:00' },
    { dow: 2 as DOW, start: '13:00', end: '17:00' },
    { dow: 3 as DOW, start: '13:00', end: '17:00' },
    { dow: 4 as DOW, start: '13:00', end: '17:00' },
    { dow: 5 as DOW, start: '13:00', end: '17:00' }
  ]},
  { name: 'Hele week 9-17', windows: [
    { dow: 1 as DOW, start: '09:00', end: '17:00' },
    { dow: 2 as DOW, start: '09:00', end: '17:00' },
    { dow: 3 as DOW, start: '09:00', end: '17:00' },
    { dow: 4 as DOW, start: '09:00', end: '17:00' },
    { dow: 5 as DOW, start: '09:00', end: '17:00' }
  ]},
  { name: 'Leegmaken', windows: [] }
]

export default function AvailabilityEditor({ windows, onChange, className = '' }: AvailabilityEditorProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState<{ dow: DOW; time: string } | null>(null)
  const [dragEnd, setDragEnd] = useState<{ dow: DOW; time: string } | null>(null)

  const { days, timeSlots } = buildWeekGrid({ uid: '', windows: [], tz: '', callEnabled: true, videoEnabled: true, updatedAt: 0 })

  const isTimeSlotSelected = useCallback((dow: DOW, time: string) => {
    return windows.some(window => 
      window.dow === dow && 
      time >= window.start && 
      time < window.end
    )
  }, [windows])

  const isTimeSlotInDrag = useCallback((dow: DOW, time: string) => {
    if (!dragStart || !dragEnd || dragStart.dow !== dow) return false
    
    const start = dragStart.time < dragEnd.time ? dragStart.time : dragEnd.time
    const end = dragStart.time < dragEnd.time ? dragEnd.time : dragStart.time
    
    return time >= start && time < end
  }, [dragStart, dragEnd])

  const handleMouseDown = (dow: DOW, time: string) => {
    setIsDragging(true)
    setDragStart({ dow, time })
    setDragEnd({ dow, time })
  }

  const handleMouseEnter = (dow: DOW, time: string) => {
    if (isDragging && dragStart?.dow === dow) {
      setDragEnd({ dow, time })
    }
  }

  const handleMouseUp = () => {
    if (isDragging && dragStart && dragEnd) {
      const start = dragStart.time < dragEnd.time ? dragStart.time : dragEnd.time
      const end = dragStart.time < dragEnd.time ? dragEnd.time : dragStart.time
      
      if (start !== end) {
        const newWindow: AvailabilityWindow = {
          dow: dragStart.dow,
          start,
          end
        }
        
        const newWindows = normalizeWindows([...windows, newWindow])
        onChange(newWindows)
      }
    }
    
    setIsDragging(false)
    setDragStart(null)
    setDragEnd(null)
  }

  const handleTimeSlotClick = (dow: DOW, time: string) => {
    if (isDragging) return
    
    const isSelected = isTimeSlotSelected(dow, time)
    
    if (isSelected) {
      // Remove this time slot from all windows
      const newWindows = windows.filter(window => 
        !(window.dow === dow && time >= window.start && time < window.end)
      )
      onChange(newWindows)
    } else {
      // Add a 30-minute window
      const endTime = new Date(`2000-01-01T${time}:00`)
      endTime.setMinutes(endTime.getMinutes() + 30)
      const end = endTime.toTimeString().slice(0, 5)
      
      const newWindow: AvailabilityWindow = { dow, start: time, end }
      const newWindows = normalizeWindows([...windows, newWindow])
      onChange(newWindows)
    }
  }

  const applyPreset = (preset: typeof PRESETS[0]) => {
    onChange(normalizeWindows(preset.windows))
  }

  const removeWindow = (index: number) => {
    const newWindows = windows.filter((_, i) => i !== index)
    onChange(newWindows)
  }

  const formatWindow = (window: AvailabilityWindow) => {
    const dayNames = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za']
    return `${dayNames[window.dow]} ${window.start}-${window.end}`
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          📅 Beschikbaarheid
        </label>
        
        {/* Presets */}
        <div className="flex flex-wrap gap-2 mb-4">
          {PRESETS.map((preset, index) => (
            <button
              key={index}
              onClick={() => applyPreset(preset)}
              className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              {preset.name}
            </button>
          ))}
        </div>

        {/* Week Grid */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-8 gap-px bg-gray-200">
            {/* Header */}
            <div className="bg-gray-50 p-2"></div>
            {days.map((day, index) => (
              <div key={day} className="bg-gray-50 p-2 text-center text-xs font-medium">
                {day}
              </div>
            ))}
            
            {/* Time slots */}
            {timeSlots.map((time) => (
              <React.Fragment key={time}>
                <div className="bg-gray-50 p-1 text-xs text-gray-500 text-right pr-2">
                  {time}
                </div>
                {days.map((_, dayIndex) => {
                  const dow = dayIndex as DOW
                  const isSelected = isTimeSlotSelected(dow, time)
                  const isInDrag = isTimeSlotInDrag(dow, time)
                  
                  return (
                    <div
                      key={`${dow}-${time}`}
                      className={`h-6 cursor-pointer transition-colors ${
                        isSelected || isInDrag
                          ? 'bg-blue-500 hover:bg-blue-600'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                      onMouseDown={() => handleMouseDown(dow, time)}
                      onMouseEnter={() => handleMouseEnter(dow, time)}
                      onMouseUp={handleMouseUp}
                      onClick={() => handleTimeSlotClick(dow, time)}
                    />
                  )
                })}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Selected Windows */}
        {windows.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Gekozen vensters:</h4>
            <div className="flex flex-wrap gap-2">
              {windows.map((window, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm"
                >
                  <span>{formatWindow(window)}</span>
                  <button
                    onClick={() => removeWindow(index)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
